/**
 * Free gift auto-add — eligibility from Liquid promo-adjusted totals
 * (buy-2 campaigns / pick-10 / native discounts), never undiscounted origin sum.
 *
 * cart.js has no product tags, so JS reads [data-free-gift-state] from
 * section HTML that Liquid already computed with cart-promo-pricing.
 *
 * Gift lines are locked at quantity 1. Extra units and extra gift lines are
 * clamped so the cart cannot hold unlimited free gifts.
 *
 * GAVEFIKS (Claude 06.10.2026):
 *  1. Utdatert gave: en _free_gift-linje med en ANNEN variant enn dagens gave
 *     (custom.free_gift_variant_id) fjernes, så riktig gave kan legges inn. Før ble en
 *     gammel gave (Blackberry) liggende i gamle handlekurver, blokkerte ny gave og ble
 *     belastet full pris i kassen.
 *  2. Kasse-sjekk: klikk på «Til kassen» (button name="checkout") holdes kort igjen mens
 *     gaven sjekkes/legges inn. Maks CHECKOUT_GUARD_TIMEOUT_MS; ved feil eller tidsavbrudd
 *     slippes kunden alltid videre — gaven skal aldri kunne blokkere et kjøp.
 *  3. Clarity-hendelser: free_gift_failed (+ tag free_gift_error) når gaven ikke kunne legges
 *     inn, free_gift_added_at_checkout når kasse-sjekken måtte redde gaven,
 *     free_gift_stale_removed når en utdatert gave ble fjernet.
 *  4. Kampanjeslutt: når ingen gave er satt opp (custom.free_gift_variant_id tom eller
 *     custom.free_gift_threshold 0), fjernes alle _free_gift-linjer fra handlekurven, så ingen
 *     kunde blir belastet full pris for en gave som ble liggende igjen.
 *     NB: Å bare skru av rabatten stopper IKKE gaven — metafeltet må tømmes.
 */
(() => {
  const PROPERTY_KEY = '_free_gift';
  const STATE_SELECTOR = '[data-free-gift-state]';
  const CHECKOUT_GUARD_TIMEOUT_MS = 3000;
  const nativeFetch = window.fetch.bind(window);
  const root = () => window.Shopify?.routes?.root || '/';

  let intercepting = false;
  let cartChangeHookDepth = 0;
  let cachedSectionId = null;
  let giftLineKeys = new Set();

  // Cart state Shopify already refused the gift for (usually no inventory left for the gift
  // variant). Retrying only makes sense once the cart holds a different amount of it.
  let giftBlockedForKey = null;

  function getConfig() {
    const config = window.themeVariables?.freeGift;
    if (!config?.variantId || !config?.thresholdCents) return null;
    return config;
  }

  function isCartPage() {
    return window.themeVariables?.settings?.pageType === 'cart';
  }

  /** Clarity custom events — no-ops when Clarity is not loaded or consent is missing. */
  function reportToClarity(eventName, tags) {
    try {
      if (typeof window.clarity !== 'function') return;
      if (tags) {
        Object.keys(tags).forEach((key) => window.clarity('set', key, String(tags[key]).slice(0, 255)));
      }
      window.clarity('event', eventName);
    } catch (error) {
      // never let reporting break the cart
    }
  }

  function reportGiftFailure(error, where) {
    const message = (error && (error.message || String(error))) || 'unknown';
    console.warn('[free-gift] gift not added:', message);
    reportToClarity('free_gift_failed', { free_gift_error: `${where}: ${message}` });
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function isCartChangeRequest(input) {
    return requestUrl(input).includes('/cart/change');
  }

  function cssEscape(value) {
    if (window.CSS?.escape) return CSS.escape(String(value));
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /**
   * A gift line is identified by its _free_gift property, never by its variant.
   * The gift product bought normally stays at catalog price and must not unlock
   * or receive a free copy of itself.
   */
  function isGiftItem(item) {
    return Boolean(item?.properties?.[PROPERTY_KEY]);
  }

  /** A _free_gift line whose variant is no longer the configured gift (earlier campaign). */
  function isStaleGiftItem(item, config) {
    if (!config || !isGiftItem(item)) return false;
    return Number(item.variant_id) !== Number(config.variantId);
  }

  function getStaleGiftItems(cart, config) {
    return (cart?.items || []).filter((item) => isStaleGiftItem(item, config));
  }

  function hasPaidGiftProduct(cart, variantId) {
    const id = Number(variantId);
    if (!id) return false;
    return (cart?.items || []).some((item) => Number(item.variant_id) === id && !isGiftItem(item));
  }

  function getGiftItems(cart) {
    return (cart?.items || []).filter(isGiftItem);
  }

  function giftInCart(cart) {
    return getGiftItems(cart).length > 0;
  }

  function rememberGiftKeys(cart) {
    giftLineKeys = new Set(getGiftItems(cart).map((item) => String(item.key)));
  }

  function seedGiftKeysFromDom() {
    document.querySelectorAll('[data-free-gift-line] [data-line-key]').forEach((input) => {
      const key = input.getAttribute('data-line-key');
      if (key) giftLineKeys.add(key);
    });

    const state = readDomState();
    if (state?.lineKey) giftLineKeys.add(String(state.lineKey));
  }

  function isGiftLineId(id) {
    if (id == null || id === '') return false;
    const key = String(id);
    if (giftLineKeys.has(key)) return true;
    return Boolean(document.querySelector(`[data-free-gift-line] [data-line-key="${cssEscape(key)}"]`));
  }

  function giftNeedsClamp(cart) {
    const gifts = getGiftItems(cart);
    if (!gifts.length) return false;
    return gifts.some((item, index) => item.quantity !== (index === 0 ? 1 : 0));
  }

  /** How many units of the gift variant the cart holds, gift line included. */
  function giftVariantSignature(cart, variantId) {
    const id = Number(variantId);
    const items = cart?.items || [];
    let quantity = 0;

    for (let i = 0; i < items.length; i++) {
      if (Number(items[i].variant_id) === id) quantity += items[i].quantity;
    }
    return `${id}:${quantity}`;
  }

  function parseStateFromHtml(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(STATE_SELECTOR);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function parseStateFromCartSections(cart) {
    const sections = cart?.sections;
    if (!sections) return null;

    const values = Object.values(sections);
    for (let i = 0; i < values.length; i++) {
      const state = parseStateFromHtml(values[i]);
      if (state?.variantId) return state;
    }
    return null;
  }

  function readDomState() {
    const nodes = document.querySelectorAll(STATE_SELECTOR);
    if (!nodes.length) return null;

    let latest = null;
    for (let i = 0; i < nodes.length; i++) {
      try {
        latest = JSON.parse(nodes[i].textContent);
      } catch (error) {
        // ignore
      }
    }
    return latest;
  }

  function resolveSectionId() {
    if (cachedSectionId) return cachedSectionId;

    const el =
      document.querySelector('cart-drawer')?.closest('.shopify-section') ||
      document.querySelector('.shopify-section--main-cart');

    if (!el?.id) return null;
    cachedSectionId = el.id.replace(/^shopify-section-/, '');
    return cachedSectionId;
  }

  /** Liquid-rendered eligibility (discounted campaign totals). */
  async function resolveGiftState(cart) {
    const fromSections = parseStateFromCartSections(cart);
    if (fromSections) return fromSections;

    const fromDom = readDomState();
    // DOM may be stale right after a cart mutation — prefer a fresh section fetch.
    const sectionId = resolveSectionId();
    if (!sectionId) return fromDom;

    try {
      const response = await nativeFetch(`${root()}?section_id=${encodeURIComponent(sectionId)}`, {
        credentials: 'same-origin',
      });
      if (!response.ok) return fromDom;
      return parseStateFromHtml(await response.text()) || fromDom;
    } catch (error) {
      return fromDom;
    }
  }

  function shouldAddGift(state, cart, config) {
    if (!config || !cart) return false;
    if (giftInCart(cart)) return false;
    if (hasPaidGiftProduct(cart, config.variantId)) return false;
    if (giftBlockedForKey && giftBlockedForKey === giftVariantSignature(cart, config.variantId)) return false;
    if (!state || state.eligible !== true) return false;
    return true;
  }

  function shouldRemoveGift(state, cart, config) {
    if (!giftInCart(cart)) return false;
    if (config && hasPaidGiftProduct(cart, config.variantId)) return true;
    if (state && state.eligible !== true) return true;
    return false;
  }

  function collectSectionIds(cart) {
    const fromCart = Object.keys(cart?.sections || {});
    if (fromCart.length) return fromCart;

    const bundled = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: bundled },
      })
    );
    return bundled;
  }

  async function fetchCart() {
    const response = await nativeFetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('cart.js failed');
    return response.json();
  }

  async function changeLineQuantity(id, quantity, sectionIds) {
    const payload = { id, quantity: Number(quantity) };

    if (sectionIds?.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const response = await nativeFetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`gift quantity clamp failed (${response.status})`);
    }

    return response.json();
  }

  async function removeGiftLines(cart, sectionIds) {
    rememberGiftKeys(cart);
    const gifts = getGiftItems(cart);
    if (!gifts.length) return { cart, changed: false };

    let current = cart;
    let changed = false;

    for (let i = 0; i < gifts.length; i++) {
      const item = gifts[i];
      const live = (current.items || []).find((line) => line.key === item.key);
      if (!live) continue;

      current = await changeLineQuantity(item.key, 0, sectionIds);
      changed = true;
    }

    rememberGiftKeys(current);
    return { cart: current, changed };
  }

  /** Remove _free_gift lines left over from an earlier gift campaign (wrong variant). */
  async function removeStaleGiftLines(cart, config, sectionIds) {
    const stale = getStaleGiftItems(cart, config);
    if (!stale.length) return { cart, changed: false };

    let current = cart;
    let changed = false;

    for (let i = 0; i < stale.length; i++) {
      const item = stale[i];
      const live = (current.items || []).find((line) => line.key === item.key);
      if (!live) continue;

      current = await changeLineQuantity(item.key, 0, sectionIds);
      changed = true;
    }

    if (changed) {
      reportToClarity('free_gift_stale_removed', { free_gift_stale_variant: stale[0].variant_id });
    }

    rememberGiftKeys(current);
    return { cart: current, changed };
  }

  async function enforceSingleGiftUnit(cart, sectionIds) {
    rememberGiftKeys(cart);
    const gifts = getGiftItems(cart);
    if (!gifts.length) return { cart, changed: false };

    let current = cart;
    let changed = false;

    for (let i = 0; i < gifts.length; i++) {
      const item = gifts[i];
      const live = (current.items || []).find((line) => line.key === item.key);
      if (!live) continue;

      const targetQty = i === 0 ? 1 : 0;
      if (live.quantity === targetQty) continue;

      current = await changeLineQuantity(item.key, targetQty, sectionIds);
      changed = true;
    }

    rememberGiftKeys(current);
    return { cart: current, changed };
  }

  async function addFreeGift(variantId, sectionIds, cart) {
    const payload = {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: { [PROPERTY_KEY]: '1' },
        },
      ],
    };

    if (sectionIds?.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const response = await nativeFetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));

      // 422 means Shopify rejected the line itself — most often the gift variant has no
      // unit left because the cart already holds its whole available stock. Remember the
      // cart state so the gift is not requested again on every page view.
      if (response.status === 422) {
        giftBlockedForKey = giftVariantSignature(cart, variantId);
      }

      throw new Error(error.description || error.message || `free gift add failed (${response.status})`);
    }

    return response.json();
  }

  async function syncGift(cart, sectionIds) {
    let current = cart;
    let mutated = false;
    let added = false;

    const config = getConfig();

    // Campaign over: no gift configured, so any leftover gift line goes.
    if (!config) {
      const removed = await removeGiftLines(current, sectionIds);
      return { cart: removed.cart, mutated: removed.changed, added: false };
    }

    // Stale gifts first: otherwise the clamp below would keep an old gift as the
    // "first" gift line and zero out the correct one.
    if (config) {
      const cleaned = await removeStaleGiftLines(current, config, sectionIds);
      current = cleaned.cart;
      mutated = cleaned.changed;
    }

    const clamped = await enforceSingleGiftUnit(current, sectionIds);
    current = clamped.cart;
    mutated = mutated || clamped.changed;

    if (config) {
      const state = await resolveGiftState(current);
      if (shouldRemoveGift(state, current, config)) {
        const removed = await removeGiftLines(current, sectionIds);
        current = removed.cart;
        mutated = mutated || removed.changed;
      } else if (shouldAddGift(state, current, config)) {
        const addResponse = await addFreeGift(config.variantId, sectionIds, current);
        current = await fetchCart();
        current.sections = addResponse.sections || current.sections || {};
        mutated = true;
        added = true;
      }
    }

    rememberGiftKeys(current);
    return { cart: current, mutated, added };
  }

  function parseJsonBody(init) {
    if (!init || typeof init.body !== 'string') return null;
    try {
      return JSON.parse(init.body);
    } catch (error) {
      return null;
    }
  }

  function rewriteGiftQuantityPayload(input, init) {
    if (!isCartChangeRequest(input)) return init;

    const payload = parseJsonBody(init);
    if (!payload) return init;

    const lineId = payload.id || payload.line;
    const qty = Number(payload.quantity);
    if (lineId == null || !Number.isFinite(qty) || qty <= 1) return init;
    if (!isGiftLineId(lineId)) return init;

    return {
      ...init,
      body: JSON.stringify({ ...payload, quantity: 1 }),
    };
  }

  function dispatchCartChange(detail) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail,
      })
    );
  }

  async function replaceMainCartSection() {
    const sectionEl = document.querySelector('.shopify-section--main-cart');
    if (!sectionEl?.id) return false;

    const sectionId = sectionEl.id.replace(/^shopify-section-/, '');
    const response = await nativeFetch(`${root()}?section_id=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) return false;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById(`shopify-section-${sectionId}`);
    if (!fresh) return false;

    sectionEl.replaceWith(fresh);
    seedGiftKeysFromDom();
    return true;
  }

  /**
   * Cart page qty change: theme reloads once.
   * Add gift first only when Liquid discounted total qualifies.
   * Gift-line quantity increases are rewritten to 1 before they reach Shopify.
   */
  window.fetch = async function patchedFetch(input, init) {
    if (cartChangeHookDepth > 0) {
      return nativeFetch(input, init);
    }

    const nextInit = rewriteGiftQuantityPayload(input, init);
    const response = await nativeFetch(input, nextInit);

    if (intercepting || !response.ok) return response;
    if (!isCartPage() || !isCartChangeRequest(input)) return response;

    const config = getConfig();
    cartChangeHookDepth += 1;
    try {
      const cart = await response.clone().json();
      if (config || giftInCart(cart)) {
        await syncGift(cart, null);
      }
    } catch (error) {
      reportGiftFailure(error, 'cart-page');
    } finally {
      cartChangeHookDepth -= 1;
    }

    return response;
  };

  /**
   * Drawer / AJAX add: use Liquid eligibility from returned sections.
   */
  document.addEventListener(
    'cart:change',
    (event) => {
      if (intercepting || event.detail?.freeGiftHandled || isCartPage()) return;

      const cart = event.detail?.cart;
      if (!cart) return;

      rememberGiftKeys(cart);
      const config = getConfig();
      const needsClamp = giftNeedsClamp(cart);
      const hasStale = getStaleGiftItems(cart, config).length > 0;
      const syncedState = parseStateFromCartSections(cart);

      if (!config) {
        if (!giftInCart(cart)) return;
      } else if (!needsClamp && !hasStale) {
        if (
          syncedState &&
          !shouldAddGift(syncedState, cart, config) &&
          !shouldRemoveGift(syncedState, cart, config)
        ) {
          return;
        }
      }

      event.stopImmediatePropagation();
      intercepting = true;
      const originalDetail = event.detail;

      (async () => {
        try {
          const sectionIds = collectSectionIds(cart);
          const result = await syncGift(cart, sectionIds);
          dispatchCartChange({
            ...originalDetail,
            cart: result.cart,
            freeGiftHandled: true,
          });
        } catch (error) {
          reportGiftFailure(error, 'drawer');
          dispatchCartChange({
            ...originalDetail,
            freeGiftHandled: true,
          });
        } finally {
          intercepting = false;
        }
      })();
    },
    true
  );

  /**
   * Checkout guard: before the visitor leaves for checkout, make sure the gift is in the
   * cart (and the right one). Always lets the visitor through — on success, on error and
   * after CHECKOUT_GUARD_TIMEOUT_MS.
   */
  function waitForIdle(maxMs) {
    return new Promise((resolve) => {
      const started = Date.now();
      (function poll() {
        if (!intercepting || Date.now() - started > maxMs) return resolve();
        setTimeout(poll, 50);
      })();
    });
  }

  async function ensureGiftBeforeCheckout() {

    await waitForIdle(1500);
    intercepting = true;
    try {
      const cart = await fetchCart();
      const result = await syncGift(cart, null);
      if (result.added) {
        reportToClarity('free_gift_added_at_checkout');
      }
      return result.mutated ? 'mutated' : 'unchanged';
    } finally {
      intercepting = false;
    }
  }

  function isCheckoutSubmit(event) {
    const submitter = event.submitter;
    return Boolean(submitter && submitter.getAttribute('name') === 'checkout');
  }

  /**
   * If the cart was changed (or we do not know), go straight to /checkout instead of
   * re-submitting the form: the form's updates[] inputs are positional and would no longer
   * line up with the cart lines after a gift line was added or removed.
   */
  function continueToCheckout(form, submitter, outcome) {
    if (outcome !== 'unchanged') {
      window.location.href = `${root()}checkout`;
      return;
    }

    form.__mmGiftChecked = true;
    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter);
      } else {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'checkout';
        hidden.value = '1';
        form.appendChild(hidden);
        form.submit();
      }
    } finally {
      // requestSubmit dispatches synchronously; reset so a later visit (back button /
      // bfcache) is guarded again.
      form.__mmGiftChecked = false;
    }
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = form.getAttribute('action') || '';
      if (!/\/cart(\/|$|\?)/.test(action)) return;

      form.querySelectorAll('[data-free-gift-line] input[name="updates[]"]').forEach((input) => {
        input.value = '1';
      });

      // No campaign and no gift line seen in this cart: nothing to check, no delay.
      if (!isCheckoutSubmit(event) || form.__mmGiftChecked) return;
      if (!getConfig() && giftLineKeys.size === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitter = event.submitter;
      submitter.setAttribute('aria-busy', 'true');

      const timeout = new Promise((resolve) => setTimeout(() => resolve('timeout'), CHECKOUT_GUARD_TIMEOUT_MS));
      const check = ensureGiftBeforeCheckout().catch((error) => {
        reportGiftFailure(error, 'checkout');
        return 'error';
      });

      Promise.race([check, timeout]).then((outcome) => {
        if (outcome === 'timeout') {
          reportToClarity('free_gift_failed', { free_gift_error: 'checkout: timeout' });
        }
        submitter.removeAttribute('aria-busy');
        continueToCheckout(form, submitter, outcome);
      });
    },
    true
  );

  /**
   * Page load: only add when Liquid discounted total qualifies.
   * Soft UI refresh — no extra full reload.
   */
  async function syncOnLoad() {
    seedGiftKeysFromDom();
    const config = getConfig();
    if (intercepting) return;

    try {
      const cart = await fetchCart();
      rememberGiftKeys(cart);

      if (!config && !giftInCart(cart)) return;

      intercepting = true;
      const sectionIds = isCartPage() ? null : collectSectionIds(cart);
      const result = await syncGift(cart, sectionIds);
      if (!result.mutated) return;

      if (isCartPage()) {
        await replaceMainCartSection();
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      if (Object.keys(result.cart.sections || {}).length) {
        dispatchCartChange({
          baseEvent: 'free-gift:sync',
          cart: result.cart,
          freeGiftHandled: true,
        });
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      reportGiftFailure(error, 'page-load');
    } finally {
      intercepting = false;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncOnLoad, { once: true });
  } else {
    syncOnLoad();
  }
})();
